import { HoverItem, HoverMenu } from '@components/helpers/HoverMenu';
import { ModDissolve } from '@components/arsenal/mods/ModDissolve';
import { ModUpgrade } from '@components/arsenal/mods/ModUpgrade';
import { ModFuse } from '@components/arsenal/mods/ModFuse';
import { useTranslation, Trans } from 'react-i18next';
import { Glitch } from '@components/helpers/Glitch';
import { IconForbid2 } from '@tabler/icons-react';
import * as Shared from '@shared/lib';
import { UserData } from '@stores';
import { useState } from 'react';

export interface ModProps {
  mod: UserData['mods'][0];
  previewLevel?: number;
  equipped?: boolean;
  count?: number;

  onClick?: () => void;
};

export function Mod({ mod, count, equipped, previewLevel, onClick }: ModProps) {
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showDissolve, setShowDissolve] = useState(false);
  const [showFuse, setShowFuse] = useState(false);

  const { t } = useTranslation(undefined, { keyPrefix: 'Mods' });

  const data = Shared.modData.find(m => m.key === mod.key);
  if (!data) return null;

  const level = previewLevel || mod.level;

  const formatModifier = (effect: Shared.ModEffect, lvl: number) => {
    return effect.modifier === 'add'
      ? Math.floor(effect.values[lvl] * 100)
      : Math.floor(effect.values[lvl] * 100) - 100;
  };

  const generateDescription = () => {
    const translateObject: Record<string, number> = {};
    let index = 0;
    for (const effect of data.effects) {
      const valueNum = index ? index + 1 : '';

      const mult = formatModifier(effect, mod.level);
      translateObject[`value${valueNum}`] = Math.abs(mult);

      const upgradeMult = formatModifier(effect, level);
      translateObject[`upgradeValue${valueNum}`] = Math.abs(upgradeMult);

      index += 1;
    }

    const key = previewLevel && previewLevel > mod.level
      ? `${mod.key}.descriptionUpgrade`
      : `${mod.key}.description`;

    return { key, values: translateObject };
  };

  const InnerMod = () => (
    <>
      {count && (
        <p className={`${count >= 4 && 'text-primary'} mod-count`}>
          {equipped ? <IconForbid2 className="text-gray-500" size={16} /> : count}
        </p>
      )}

      <div className="flex-1">
        <p className="mod-title">
          <Glitch>{t(`${mod.key}.name`)}</Glitch>
        </p>
        <div className="mod-levels">
          {Array.from({ length: 4 + mod.rank * 2 }).map((_, i) => (
            <div className={`${level >= i && 'is-active'} mod-level`} key={i} />
          ))}
        </div>
        <p className="mod-description">
          <Trans
            components={{ mut: <span className="font-bold text-green-500" /> }}
            values={generateDescription().values}
            i18nKey={generateDescription().key}
            t={t}
          />
        </p>
      </div>
      <div className="mod-type">
        <p>{t(data.type)}</p>
      </div>
    </>
  );

  const hoverItems: HoverItem[] = [
    { label: t('upgrade'), onClick: () => setShowUpgrade(true) },
    { label: t('fuse'), onClick: () => setShowFuse(true), visible: !equipped },
    { label: t('dissolve'), onClick: () => setShowDissolve(true), visible: !equipped },
  ];

  return (
    <>
      {!previewLevel && (
        <>
          <ModDissolve onClose={() => setShowDissolve(false)} open={showDissolve} mod={mod} />
          <ModUpgrade onClose={() => setShowUpgrade(false)} open={showUpgrade} mod={mod} />
          <ModFuse onClose={() => setShowFuse(false)} open={showFuse} mod={mod} />
        </>
      )}

      {onClick && (
        <button className={`mod mod-${mod.rank}`} onClick={onClick}>
          <InnerMod />
        </button>
      )}

      {!onClick && (
        <HoverMenu className={`mod mod-${mod.rank}`} items={hoverItems}>
          <InnerMod />
        </HoverMenu>
      )}
    </>
  );
}